/**
 * BookingNotesPanel.tsx
 * Panel zum Verwalten von Notizen zu einer Buchung
 */

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ChatBubbleBottomCenterTextIcon,
  PlusIcon,
  BellIcon,
  UserIcon,
  ClockIcon,
  FunnelIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { BookingNote } from '../../types/booking.types';
import { adminService } from '../../services/adminService';
import { format } from 'date-fns';
import './BookingNotesPanel.css';

interface BookingForNotes {
  id: string;
  bookingNumber: string;
  customerName: string;
  [key: string]: any;
}

interface BookingNotesPanelProps {
  booking: BookingForNotes | null;
  isOpen: boolean;
  onClose: () => void;
  onAddNote: (note: Partial<BookingNote>) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
}

const BookingNotesPanel: React.FC<BookingNotesPanelProps> = ({
  booking,
  isOpen,
  onClose,
  onAddNote,
  onDeleteNote
}) => {
  // State
  const [notes, setNotes] = useState<BookingNote[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteType, setNewNoteType] = useState<'internal' | 'customer' | 'system'>('internal');
  const [isReminder, setIsReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (isOpen && booking) {
      loadNotes();
      resetForm();
    }
  }, [isOpen, booking]);

  if (!isOpen || !booking) return null;

  // Load notes from API
  const loadNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminService.getBookingNotes(parseInt(booking.id));
      const transformedNotes: BookingNote[] = (Array.isArray(response) ? response : []).map((dto: any) => ({
        id: dto.id.toString(),
        bookingId: dto.bookingId.toString(),
        text: dto.text,
        type: dto.type,
        createdBy: dto.createdBy,
        createdAt: dto.createdAt,
        isReminder: dto.isReminder,
        reminderDate: dto.reminderDate
      }));
      setNotes(transformedNotes);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Fehler beim Laden der Notizen');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewNoteText('');
    setNewNoteType('internal');
    setIsReminder(false);
    setReminderDate('');
    setError(null);
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      setError('Notiz darf nicht leer sein');
      return;
    }

    if (isReminder && !reminderDate) {
      setError('Bitte Erinnerungsdatum wählen');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format reminderDate as ISO datetime string for backend
      let formattedReminderDate: string | undefined;
      if (isReminder && reminderDate) {
        // Convert "2026-01-14" to "2026-01-14T00:00:00.000Z"
        formattedReminderDate = new Date(reminderDate + 'T12:00:00').toISOString();
      }

      const newNote: Partial<BookingNote> = {
        bookingId: booking.id,
        text: newNoteText.trim(),
        type: newNoteType,
        isReminder,
        reminderDate: formattedReminderDate,
        createdBy: 'Admin',
        createdAt: new Date().toISOString()
      };

      await onAddNote(newNote);
      await loadNotes();
      resetForm();
    } catch (err: any) {
      console.error('Error adding note:', err);
      setError(err.message || 'Fehler beim Hinzufügen');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete note
  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Notiz wirklich löschen?')) return;

    try {
      await adminService.deleteBookingNote(parseInt(booking.id), parseInt(noteId));
      await loadNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Fehler beim Löschen');
    }
  };

  // Filter notes
  const filteredNotes = filterType === 'all'
    ? notes
    : notes.filter(note => note.type === filterType);

  // Get badge class
  const getBadgeClass = (type: string) => {
    switch (type) {
      case 'internal': return 'note-badge-internal';
      case 'customer': return 'note-badge-customer';
      case 'system': return 'note-badge-system';
      default: return 'note-badge-system';
    }
  };

  const getBadgeLabel = (type: string) => {
    switch (type) {
      case 'internal': return 'Intern';
      case 'customer': return 'Kunde';
      case 'system': return 'System';
      default: return type;
    }
  };

  return (
    <div className="notes-dialog-overlay" onClick={onClose}>
      <div className="notes-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="notes-dialog-header">
          <div className="notes-dialog-header-title">
            <ChatBubbleBottomCenterTextIcon />
            <div>
              <h3>Notizen</h3>
              <p>{booking.bookingNumber} - {booking.customerName}</p>
            </div>
          </div>
          <button onClick={onClose} className="notes-dialog-close" disabled={loading}>
            <XMarkIcon />
          </button>
        </div>

        {/* Body */}
        <div className="notes-dialog-body">
          {/* Error Display */}
          {error && (
            <div className="notes-error-box">
              <ExclamationTriangleIcon />
              <span>{error}</span>
            </div>
          )}

          {/* Add Note Form */}
          <div className="notes-add-form">
            <div className="notes-add-form-title">Neue Notiz hinzufügen</div>

            <textarea
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Notiz eingeben..."
              rows={3}
              className="notes-textarea"
              disabled={loading}
            />

            <div className="notes-form-row">
              <div className="notes-form-group">
                <label>Notiz-Typ</label>
                <select
                  value={newNoteType}
                  onChange={(e) => setNewNoteType(e.target.value as any)}
                  className="notes-form-select"
                  disabled={loading}
                >
                  <option value="internal">Intern (nur Admins)</option>
                  <option value="customer">Kunde (sichtbar)</option>
                  <option value="system">System</option>
                </select>
              </div>

              <div className="notes-form-group">
                <label className="notes-reminder-label">
                  <input
                    type="checkbox"
                    checked={isReminder}
                    onChange={(e) => setIsReminder(e.target.checked)}
                    disabled={loading}
                  />
                  <BellIcon />
                  Erinnerung
                </label>
                {isReminder && (
                  <input
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="notes-form-input"
                    disabled={loading}
                  />
                )}
              </div>
            </div>

            <button
              onClick={handleAddNote}
              disabled={loading || !newNoteText.trim()}
              className="notes-btn-add"
            >
              <PlusIcon />
              Notiz hinzufügen
            </button>
          </div>

          {/* Filter */}
          <div className="notes-filter">
            <FunnelIcon />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="notes-filter-select"
            >
              <option value="all">Alle ({notes.length})</option>
              <option value="internal">Intern ({notes.filter(n => n.type === 'internal').length})</option>
              <option value="customer">Kunde ({notes.filter(n => n.type === 'customer').length})</option>
              <option value="system">System ({notes.filter(n => n.type === 'system').length})</option>
            </select>
          </div>

          {/* Notes List */}
          <div className="notes-list">
            {loading && notes.length === 0 ? (
              <div className="notes-loading">
                <div className="notes-spinner"></div>
              </div>
            ) : filteredNotes.length === 0 ? (
              <div className="notes-empty">
                <ChatBubbleBottomCenterTextIcon />
                <p>Keine Notizen vorhanden</p>
              </div>
            ) : (
              filteredNotes.map((note) => (
                <div key={note.id} className="note-item">
                  <div className="note-item-header">
                    <div className="note-item-badges">
                      <span className={`note-badge ${getBadgeClass(note.type)}`}>
                        {getBadgeLabel(note.type)}
                      </span>
                      {note.isReminder && note.reminderDate && (
                        <span className="note-badge note-badge-reminder">
                          <BellIcon />
                          {format(new Date(note.reminderDate), 'dd.MM.yyyy')}
                        </span>
                      )}
                    </div>
                    {onDeleteNote && (
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="note-delete-btn"
                        title="Löschen"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>

                  <p className="note-item-text">{note.text}</p>

                  <div className="note-item-footer">
                    <span>
                      <UserIcon />
                      {note.createdBy}
                    </span>
                    <span>
                      <ClockIcon />
                      {format(new Date(note.createdAt), 'dd.MM.yyyy HH:mm')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="notes-dialog-footer">
          <button onClick={onClose} className="notes-btn-close">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingNotesPanel;
